import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { LoadingState } from '@/components/common/LoadingState';
import { PORTAL_ROUTES } from '@/routes/paths';
import { usePortalAuthStore } from '@/store/portal-auth.store';

/**
 * Customer portal route guards (Phase 6).
 *
 * Separate from the ERP guards because they read a different store and redirect
 * to a different sign-in. A customer bounced to `/login` would land on the staff
 * screen, which is both confusing and a small disclosure that an internal
 * application exists behind the same host.
 *
 * These guards decide what *renders*. They are not the security boundary — that
 * is `requirePortalCustomer` on the API, plus the customer scope in every query.
 * A customer who edits the URL, or calls the API directly with curl, is stopped
 * by the server; these only keep the interface honest.
 */

/**
 * Bootstraps the portal session lazily, on first entry to `/portal`.
 *
 * The ERP store bootstraps at app start; this one deliberately does not, because
 * doing both would mean two refresh calls on every page load and two stores
 * racing for the single in-memory access token. A visitor reaches exactly one of
 * the two branches, so exactly one session is restored.
 */
const usePortalBootstrap = (): void => {
  const status = usePortalAuthStore((state) => state.status);
  const bootstrap = usePortalAuthStore((state) => state.bootstrap);

  useEffect(() => {
    if (status === 'initialising') void bootstrap();
  }, [status, bootstrap]);
};

/** Requires a portal session. Remembers the attempted URL for after sign-in. */
export const PortalAuthGuard = () => {
  const status = usePortalAuthStore((state) => state.status);
  const location = useLocation();
  usePortalBootstrap();

  if (status === 'initialising') {
    return <LoadingState variant="page" label="Loading your account…" />;
  }

  if (status === 'unauthenticated') {
    return <Navigate to={PORTAL_ROUTES.login} replace state={{ from: location }} />;
  }

  return <Outlet />;
};

/** The sign-in screen: an already-signed-in customer goes to their overview. */
export const PortalGuestGuard = () => {
  const status = usePortalAuthStore((state) => state.status);
  usePortalBootstrap();

  if (status === 'initialising') return <LoadingState variant="page" label="Loading…" />;
  if (status === 'authenticated') return <Navigate to={PORTAL_ROUTES.dashboard} replace />;

  return <Outlet />;
};
